import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  model,
  signal,
} from '@angular/core';

import { generateId } from '@lc/util';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ControlValueAccessor, FormControl, ValidationErrors, Validator } from '@angular/forms';
import { BspkTextInput } from '../text-input';
import { BspkFormField } from '../form-field';
import { BspkTimepickerContainer, TimePeriod } from '../timepicker-container';
import { noop, provideValidator, provideValueAccessor, TextInputSize } from '../util';
import { BspkButton } from '../button';
import { ControlValidator } from '../util/control-validator';

export type TimeValue = {
  /**
   * An integer between 0 and 23 representing the hour in 24-hour format.
   */
  hours: number | null;
  minutes: number | null;
};

const validTime = /^(1[0-2]|0?[1-9]):([0-5][0-9]) (AM|PM)$/i;

@Component({
  selector: 'bspk-timepicker-field',
  imports: [
    BspkTextInput,
    BspkFormField,
    BspkTimepickerContainer,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    BspkButton,
  ],
  templateUrl: './timepicker-field.component.html',
  styleUrl: './timepicker-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideValueAccessor(BspkTimepickerField), provideValidator(BspkTimepickerField)],
})
export class BspkTimepickerField extends ControlValidator implements ControlValueAccessor, Validator {
  readonly value = model<TimeValue>({ hours: null, minutes: null });
  readonly label = input.required<string>();
  readonly helperText = input<string>();
  readonly size = input<TextInputSize>('medium');
  readonly controlId = input(generateId('timepicker-field'));
  readonly required = input(false, { transform: booleanAttribute });
  readonly invalid = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly readOnly = input(false, { transform: booleanAttribute });
  readonly errorMessage = input<string>('');

  readonly isOpen = signal(false);
  readonly hours12 = computed(() => this.getHours12(this.value().hours));
  readonly inputValue = linkedSignal({
    source: () => this.value(),
    computation: ({ hours, minutes }) => {
      if (hours === null && minutes === null) return '';

      return `${this.hours12() ?? '--'}:${(minutes ?? '--')
        .toString()
        .padStart(2, '0')} ${this.period()}`;
    },
  });
  readonly period = linkedSignal({
    source: () => this.value(),
    computation: ({ hours }) => {
      if (hours === null) return 'PM';
      return hours < 12 ? 'AM' : 'PM';
    },
  });

  private onTouched: () => void = noop;

  close() {
    this.isOpen.set(false);
    this.onTouched();
  }

  open() {
    if (this.disabled() || this.readOnly()) return;
    this.isOpen.set(true);
  }

  setMinutes(minutes: number | null) {
    if (this.disabled() || this.readOnly()) return;
    this.errorState.set('');
    this.value.update((prev) => ({ ...prev, minutes }));
  }

  setHours(hours12: number | null) {
    if (this.disabled() || this.readOnly()) return;
    this.errorState.set('');
    this.value.update((prev) => ({ ...prev, hours: this.getHours24(hours12) }));
  }

  getHours24(hours12: number | null): number | null {
    if (hours12 === null) return null;

    // Convert 12-hour format to 24-hour format
    return (hours12 % 12) + (this.period() === 'PM' ? 12 : 0);
  }

  getHours12(hours24: number | null): number | null {
    if (hours24 === null) return null;

    // Convert 24-hour format to 12-hour format
    return hours24 % 12 || 12; // Convert 0 to 12 for 12-hour format
  }

  setPeriod(period: TimePeriod) {
    if (this.disabled() || this.readOnly()) return;
    this.errorState.set('');
    const { hours: hours24 } = this.value();
    if (hours24 === null) {
      this.period.set(period);
    } else {
      const newHours = period === 'AM' ? hours24 % 12 : (hours24 % 12) + 12;
      this.value.set({ ...this.value(), hours: newHours });
    }
  }

  handleInput(inputValue: string) {
    if (this.disabled() || this.readOnly()) return;
    const match = validTime.exec(inputValue);
    if (match) {
      this.errorState.set('');
      const [_, hours, minutes, period] = match;
      this.setPeriod(period.toUpperCase() as TimePeriod);
      const hours24 = this.getHours24(Number(hours));
      this.value.set({
        hours: hours24,
        minutes: Number(minutes),
      });
    } else {
      this.errorState.set('Enter a valid time');
    }
  }

  handleBlur() {
    this.onTouched();
  }

  // ControlValueAccessor interface methods
  writeValue(value: any): void {
    this.value.set(value ?? { hours: null, minutes: null });
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean) {
    // noop
  }

  registerOnChange(fn: any) {
    this.value.subscribe(fn);
  }
  // End ControlValueAccessor interface methods

  // Validator interface methods
  registerOnValidatorChange(_fn: () => void): void {}

  validate(_control: FormControl<TimeValue>): ValidationErrors | null {
    const value = this.value();
    if (this.required() && value.hours === null && value.minutes === null) {
      return { required: true };
    }

    if (value.hours !== null && (value.hours < 0 || value.hours > 23)) {
      return { invalidTime: true };
    }

    if (value.minutes !== null && (value.minutes < 0 || value.minutes > 59)) {
      return { invalidTime: true };
    }

    return null;
  }
  // End Validator interface methods
}
